import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { ScrollView, StyleSheet, View, RefreshControl, ActivityIndicator, Text, Animated } from 'react-native';
import { Alert } from '../../utils/toast';
import { useNavigation, CompositeNavigationProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import FeaturedFeedPost from '../../components/feed/FeaturedFeedPost';
import PollPost from '../../components/feed/PollPost';
import LinkPost from '../../components/feed/LinkPost';
import FeedPostSkeleton from '../../components/common/FeedPostSkeleton';
import TopBar from '../../components/layout/TopBar';
import { getFeed, Post, likePost, unlikePost, voteOnPoll, deletePost } from '../../lib/api/feed';
import { storage } from '../../utils/storage';
import { HomeStackParamList } from '../../navigation/HomeNavigator';
import { RootStackParamList } from '../../types/navigation';

type FeedTabNavigationProp = CompositeNavigationProp<
  StackNavigationProp<HomeStackParamList>,
  StackNavigationProp<RootStackParamList>
>;

export interface FeedTabRef {
  refresh: () => void;
}

interface FeedTabProps {
  headerComponent?: React.ReactNode;
  showTopBar?: boolean;
}

const FeedTab = forwardRef<FeedTabRef, FeedTabProps>(({ headerComponent, showTopBar = false }, ref) => {
  const { theme, themeMode } = useTheme();
  const { user } = useAuth();
  const navigation = useNavigation<FeedTabNavigationProp>();
  
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [showInitialSkeleton, setShowInitialSkeleton] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(0);
  const scrollY = useRef(new Animated.Value(0)).current;
  const scrollViewRef = useRef<ScrollView>(null);
  
  const headerTranslate = useMemo(() => {
    if ((!showTopBar && !headerComponent) || headerHeight === 0) {
      return 0;
    }
    return scrollY.interpolate({
      inputRange: [0, headerHeight],
      outputRange: [0, -headerHeight],
      extrapolate: 'clamp',
    });
  }, [scrollY, showTopBar, headerComponent, headerHeight]);

  console.log('🎯 [FeedTab] RENDER - loading:', loading, 'posts.length:', posts.length, 'error:', error, 'hasInitiallyLoaded:', hasInitiallyLoaded, 'showInitialSkeleton:', showInitialSkeleton);

  const loadFeed = useCallback(async (pageNum: number = 1, isRefresh: boolean = false) => {
    const token = await storage.getToken();
    console.log('📡 [FeedTab] loadFeed START:', { pageNum, isRefresh, hasToken: !!token, currentLoading: loading, currentPostsCount: posts.length });
    
    const startTime = Date.now();
    
    try {
      if (isRefresh) {
        console.log('🔄 [FeedTab] Setting refreshing = true');
        setRefreshing(true);
      } else if (pageNum === 1) {
        console.log('⏳ [FeedTab] Setting loading = true');
        setLoading(true);
        setShowInitialSkeleton(true);
      }
      
      setError(null);
      
      console.log('[FeedTab] Calling getFeed API...');
      const response = await getFeed(token || undefined, pageNum, 10);
      console.log('[FeedTab] API Response:', { 
        postsCount: response.posts.length, 
        total: response.pagination.total,
        hasMore: response.pagination.hasMore,
        firstPost: response.posts[0]
      });
      
      // Ensure minimum 800ms loading time to show skeleton
      const elapsed = Date.now() - startTime;
      console.log('⏱️ [FeedTab] API took', elapsed, 'ms - adding delay if needed');
      if (elapsed < 800) {
        console.log('💤 [FeedTab] Waiting', (800 - elapsed), 'ms to show skeleton...');
        await new Promise(resolve => setTimeout(resolve, 800 - elapsed));
        console.log('✅ [FeedTab] Delay complete');
      }
      
      if (isRefresh || pageNum === 1) {
        console.log('[FeedTab] Setting posts (replace)');
        setPosts(response.posts);
        setHasInitiallyLoaded(true);
        setTimeout(() => setShowInitialSkeleton(false), 200);
      } else {
        console.log('[FeedTab] Setting posts (append)');
        setPosts(prev => [...prev, ...response.posts]);
      }
      
      setHasMore(response.pagination.hasMore);
      setPage(pageNum);
    } catch (err) {
      console.error('[FeedTab] Error loading feed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feed');
      setShowInitialSkeleton(false);
    } finally {
      console.log('🏁 [FeedTab] Setting loading = false, refreshing = false');
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const onRefresh = useCallback(() => {
    loadFeed(1, true);
  }, [loadFeed]);

  const handleLike = useCallback(async (postId: string, isLiked: boolean) => {
    const token = await storage.getToken();
    if (!token) return;
    
    console.log('[FeedTab] handleLike:', { postId, isLiked, action: isLiked ? 'unlike' : 'like' });
    
    try {
      // Optimistic update
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              isLiked: !isLiked, 
              likesCount: isLiked ? post.likesCount - 1 : post.likesCount + 1 
            }
          : post
      ));

      if (isLiked) {
        const result = await unlikePost(token, postId);
        console.log('[FeedTab] Unlike result:', result);
      } else {
        const result = await likePost(token, postId);
        console.log('[FeedTab] Like result:', result);
      }
    } catch (err) {
      console.error('Error toggling like:', err);
      // Revert on error
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              isLiked, 
              likesCount: isLiked ? post.likesCount + 1 : post.likesCount - 1 
            }
          : post
      ));
    }
  }, []);

  const handleVote = useCallback(async (postId: string, optionId: string) => {
    const token = await storage.getToken();
    if (!token) return;
    
    console.log('[FeedTab] Voting on poll:', { postId, optionId });
    
    // Optimistic update - immediately update UI
    setPosts(prev => prev.map(post => {
      if (post.id === postId && post.poll) {
        const updatedOptions = post.poll.options.map(opt => ({
          ...opt,
          votesCount: opt.id === optionId ? opt.votesCount + 1 : opt.votesCount,
        }));
        
        return {
          ...post,
          poll: {
            ...post.poll,
            options: updatedOptions,
            totalVotes: post.poll.totalVotes + 1,
            userVote: optionId,
          },
        };
      }
      return post;
    }));
    
    try {
      const result = await voteOnPoll(token, postId, optionId);
      console.log('[FeedTab] Vote result:', result);
      
      // Update with actual server data
      setPosts(prev => prev.map(post => 
        post.id === postId && post.poll
          ? { ...post, poll: result.poll }
          : post
      ));
    } catch (err) {
      console.error('Error voting on poll:', err);
      
      // Revert optimistic update on error
      setPosts(prev => prev.map(post => {
        if (post.id === postId && post.poll) {
          const revertedOptions = post.poll.options.map(opt => ({
            ...opt,
            votesCount: opt.id === optionId ? opt.votesCount - 1 : opt.votesCount,
          }));
          
          return {
            ...post,
            poll: {
              ...post.poll,
              options: revertedOptions,
              totalVotes: post.poll.totalVotes - 1,
              userVote: undefined,
            },
          };
        }
        return post;
      }));
    }
  }, []);

  const handleDelete = useCallback(async (postId: string) => {
    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Error', 'You must be logged in to delete posts');
        return;
      }

      Alert.alert(
        'Delete Post',
        'Are you sure you want to delete this post?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await deletePost(token, postId);
                setPosts(prev => prev.filter(p => p.id !== postId));
                Alert.alert('Success', 'Post deleted successfully');
              } catch (error) {
                console.error('[FeedTab] Delete error:', error);
                Alert.alert('Error', error instanceof Error ? error.message : 'Failed to delete post');
              }
            },
          },
        ]
      );
    } catch (error) {
      console.error('[FeedTab] Delete error:', error);
      Alert.alert('Error', 'Failed to delete post');
    }
  }, []);

  const handleEdit = useCallback((post: Post) => {
    console.log('[FeedTab] Navigating to edit post:', post.id);
    navigation.navigate('CreatePost', { mode: 'edit', post });
  }, [navigation]);

  const handleUserPress = useCallback((userId: string) => {
    console.log('[FeedTab] Navigating to profile, userId:', userId);
    navigation.navigate('Profile', { userId });
  }, [navigation]);

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: () => {
      console.log('[FeedTab] Refresh called from parent');
      loadFeed(1, true);
    },
  }), [loadFeed]);

  useEffect(() => {
    console.log('[FeedTab] Component mounted, calling loadFeed...');
    // Use requestAnimationFrame to ensure skeleton renders before loading
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        loadFeed(1);
      });
    });
  }, [loadFeed]);

  useEffect(() => {
    console.log(`[FeedTab] State updated -> themeMode=${themeMode}, totalPosts=${posts.length}, loading=${loading}, error=${error}`);
  }, [themeMode, posts.length, loading, error]);

  // Helper function to format numbers
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <View style={{ flex: 1 }}>
      {(showTopBar || headerComponent) ? (
        <Animated.View
          style={[styles.headerOverlay, { transform: [{ translateY: headerTranslate }] }]}
          onLayout={(event) => {
            const height = event.nativeEvent.layout.height;
            if (height !== headerHeight) {
              setHeaderHeight(height);
            }
          }}
        >
          {showTopBar && <TopBar title="Home" showLogo={true} />}
          {headerComponent}
        </Animated.View>
      ) : null}
      {showInitialSkeleton && (
        <View style={[StyleSheet.absoluteFillObject, { zIndex: 5, backgroundColor: theme.background, paddingTop: headerHeight }]}>
          <ScrollView style={{ paddingHorizontal: 20, paddingVertical: 32 }}>
            <FeedPostSkeleton />
            <FeedPostSkeleton />
            <FeedPostSkeleton />
          </ScrollView>
        </View>
      )}
      {(() => {
        console.log('🎨 [FeedTab] Rendering decision - loading:', loading, 'posts.length:', posts.length, 'error:', error, 'hasInitiallyLoaded:', hasInitiallyLoaded, 'showInitialSkeleton:', showInitialSkeleton);

        if (error && posts.length === 0) {
          console.log('❌ [FeedTab] SHOWING ERROR');
          return (
            <View style={[styles.centerContainer, { backgroundColor: theme.background }]}> 
              <Text style={[styles.errorText, { color: theme.error }]}> 
                {error}
              </Text>
              <Text 
                style={[styles.retryText, { color: theme.primary }]} 
                onPress={() => loadFeed(1)} 
              >
                Tap to retry
              </Text>
            </View>
          );
        }

        console.log('📜 [FeedTab] SHOWING POSTS');
        return (
        <Animated.ScrollView 
          ref={scrollViewRef}
          style={[styles.container, { backgroundColor: theme.background }]}
          contentContainerStyle={[
            styles.scrollContent,
            (showTopBar || headerComponent) ? { paddingTop: headerHeight + 16 } : null,
          ]}
          showsVerticalScrollIndicator={true}
          scrollEventThrottle={16}
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
            { useNativeDriver: true }
          )}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={theme.primary}
              colors={[theme.primary]}
            />
          }
        >
          {posts.map((post, index) => {
            console.log('[FeedTab] Rendering post:', { id: post.id, type: post.type, index });
            const isLast = index === posts.length - 1;
            
            if (post.type === 'text' || post.type === 'image') {
              return (
                <FeaturedFeedPost 
                  key={post.id}
                  post={{
                    id: post.id,
                    userId: post.userId,
                    user: post.user.name || 'Anonymous',
                    location: post.user.profession || '',
                    avatar: post.user.photo || undefined,
                    caption: post.text || '',
                    image: post.imageUrl || undefined,
                    likes: formatNumber(post.likesCount),
                    comments: formatNumber(post.commentsCount),
                    isLiked: post.isLiked,
                    createdAt: post.createdAt,
                  }}
                  currentUserId={user?.userId}
                  isAdmin={user?.role === 'admin'}
                  onLike={() => handleLike(post.id, post.isLiked)}
                  onDelete={() => handleDelete(post.id)}
                  onEdit={() => handleEdit(post)}
                  onUserPress={handleUserPress}
                  onCommentPress={() => navigation.navigate('PostDetail', { post })}
                  style={!isLast ? styles.postSpacing : undefined}
                />
              );
            } else if (post.type === 'poll' && post.poll) {
              return (
                <PollPost 
                  key={post.id}
                  post={{
                    id: post.id,
                    userId: post.userId,
                    user: post.user.name || 'Anonymous',
                    location: post.user.profession || '',
                    avatar: post.user.photo || undefined,
                    caption: post.poll.question,
                    pollOptions: post.poll.options.map(opt => ({
                      id: opt.id,
                      text: opt.text,
                      votes: opt.votesCount,
                    })),
                    totalVotes: post.poll.totalVotes,
                    likes: formatNumber(post.likesCount),
                    comments: formatNumber(post.commentsCount),
                    userVote: post.poll.userVote,
                    isLiked: post.isLiked,
                    createdAt: post.createdAt,
                  }}
                  currentUserId={user?.userId}
                  isAdmin={user?.role === 'admin'}
                  onVote={(optionId) => handleVote(post.id, optionId)}
                  onLike={() => handleLike(post.id, post.isLiked)}
                  onDelete={() => handleDelete(post.id)}
                  onEdit={() => handleEdit(post)}
                  onUserPress={handleUserPress}
                  onCommentPress={() => navigation.navigate('PostDetail', { post })}
                  style={!isLast ? styles.postSpacing : undefined}
                />
              );
            } else if (post.type === 'link' && post.linkUrl) {
              return (
                <LinkPost 
                  key={post.id}
                  post={{
                    id: post.id,
                    userId: post.userId,
                    user: post.user.name || 'Anonymous',
                    location: post.user.profession || '',
                    avatar: post.user.photo || undefined,
                    caption: post.text || '',
                    link: post.linkUrl,
                    likes: formatNumber(post.likesCount),
                    comments: formatNumber(post.commentsCount),
                    isLiked: post.isLiked,
                    createdAt: post.createdAt,
                  }}
                  currentUserId={user?.userId}
                  isAdmin={user?.role === 'admin'}
                  onLike={() => handleLike(post.id, post.isLiked)}
                  onDelete={() => handleDelete(post.id)}
                  onEdit={() => handleEdit(post)}
                  onUserPress={handleUserPress}
                  onCommentPress={() => navigation.navigate('PostDetail', { post })}
                  style={!isLast ? styles.postSpacing : undefined}
                />
              );
            }
            return null;
          })}
          
          {posts.length === 0 && !loading && (
            <View style={styles.emptyContainer}>
              <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
                No posts yet. Be the first to share!
              </Text>
            </View>
          )}
        </Animated.ScrollView>
        );
      })()}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 10,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 100,
  },
  postSpacing: {
    marginBottom: 24,
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.3)',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 12,
  },
  retryText: {
    fontSize: 16,
    fontWeight: '600',
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    textAlign: 'center',
  },
});

export default FeedTab;
